import React, { useContext, useState, useEffect } from "react";
import { Box, useTheme } from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import { ReactComponent as HomeIcon } from "../images/svg/home.svg";
import { ReactComponent as HomeActiveIcon } from "../images/svg/homeActive.svg";
import { ReactComponent as SettingsIcon } from "../images/svg/settings.svg";
import { ReactComponent as SettingsActiveIcon } from "../images/svg/settingsActive.svg";
import { ReactComponent as DatabaseIcon } from "../images/svg/database.svg";
import { ReactComponent as DatabaseActiveIcon } from "../images/svg/databaseActive.svg";
import { ReactComponent as AdminIcon } from "../images/svg/admin.svg";
import { ReactComponent as AdminActiveIcon } from "../images/svg/adminActive.svg";
import { AuthContext } from "../contexts/AuthContext";
import { useColourMode } from "../contexts/ColourContext";
import { hasAccess } from "../functions";

const MenuBar = () => {
  const [isScrolling, setIsScrolling] = useState(false);
  const [opacity, setOpacity] = useState(1);
  const location = useLocation();
  const { loggedIn, isLoggedIn } = useContext(AuthContext);
  const { accentColour } = useColourMode();
  const colourTheme = useTheme();

  let scrollTimeout = null;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolling(true);
      setOpacity(0);

      if (scrollTimeout) clearTimeout(scrollTimeout);

      scrollTimeout = setTimeout(() => {
        setIsScrolling(false);
        setOpacity(1);
      }, 100);
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      if (scrollTimeout) clearTimeout(scrollTimeout);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Function to scroll to the top of the page
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const menuButtons = [
    // Home
    {
      label: "Home",
      button: HomeIcon,
      buttonActive: HomeActiveIcon,
      path: "/",
    },

    // Settings
    ...(hasAccess(isLoggedIn.accessLevel, ["admin", "data", "user", "omni"])
      ? [
          {
            label: "Settings",
            button: SettingsIcon,
            buttonActive: SettingsActiveIcon,
            path: "/settings",
          },
        ]
      : []),

    // Admin
    ...(hasAccess(isLoggedIn.accessLevel, ["admin", "omni"])
      ? [
          {
            label: "Admin",
            button: AdminIcon,
            buttonActive: AdminActiveIcon,
            path: "/admin",
          },
        ]
      : []),

    // Database
    ...(hasAccess(isLoggedIn.accessLevel, ["admin", "data", "omni"])
      ? [
          {
            label: "Data",
            button: DatabaseIcon,
            buttonActive: DatabaseActiveIcon,
            path: "/data",
          },
        ]
      : []),
  ];

  return (
    <>
      {loggedIn && (
        <Box
          sx={{
            position: "fixed",
            width: "100dvw",
            padding: "0.5rem",
            bottom: 0,
            left: "50%",
            transform: "translateX(-50%)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              width: "95%",
              maxWidth: "500px",
              height: "100%",
              borderRadius: "1rem",
              background: `linear-gradient(
                to right bottom,
                rgba(120, 120, 120, 0.5),
                rgba(120, 120, 120, 0.2)
              )`,
              boxShadow: "0 0 1rem rgba(0, 0, 0, 0.2)",
              justifyContent: "space-around",
              alignItems: "center",
              backdropFilter: "blur(1rem)",
              zIndex: 1300,
              m: "auto",
              padding: "0.25rem",
              opacity: opacity,
              transition: isScrolling ? "opacity 0s" : "opacity 1s",
            }}
          >
            {menuButtons.map((item, index) => {
              // Check if current path matches the button's path
              const isActive =
                item.path === "/"
                  ? location.pathname === "/"
                  : location.pathname.startsWith(item.path);
              const Icon = isActive ? item.buttonActive : item.button; // Choose active or regular icon

              return (
                <Box
                  key={index}
                  component={Link}
                  to={item.path}
                  onClick={scrollToTop}
                  sx={{
                    position: "relative",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    color: isActive
                      ? accentColour
                      : colourTheme.palette.text.primary,
                    textDecoration: "none",
                    fontSize: "0.5rem",
                  }}
                >
                  <Icon
                    style={{
                      width: "1.25rem",
                      height: "1.25rem",
                      marginTop: "0.5rem",
                      marginBottom: "0.25rem",
                    }}
                  />
                  <span>{item.label}</span>
                  {/* Render Badge if badgeCount exists */}
                  {item.badgeCount > 0 && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: "0",
                        right: -8,
                        backgroundColor: "red",
                        color: "white",
                        borderRadius: "50%",
                        width: "1rem",
                        height: "1rem",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "0.65rem",
                        fontWeight: "bold",
                      }}
                    >
                      {item.badgeCount}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </>
  );
};
export default MenuBar;
